import { AssistantScreenContext, AssistantScreenDetail } from "ai-assistant/screen-context/assistant-screen-context";
import { useAssistantScreenContext } from "ai-assistant/screen-context/assistant-screen-context-provider";
import { ProposalSidePanel } from "work/proposals/_views/_shared/proposal-page-panels";

/**
 * The tab route segment all three proposal pages share. Each page has its own `PageTab` enum with
 * these same values, so this is keyed on the value rather than on any one enum.
 */
const tabNames: Record<string, string> = {
  details: "Details",
  team: "Vendor team",
  "client-team": "Client team",
  "fee-schedule": "Fee schedule",
  conflicts: "Conflicts",
  policies: "Policies",
  discount: "Discount",
};

// The names the reader sees, so the assistant and the screen use one word for one panel. The
// comments pane is "Comments" on every proposal page (#5818).
const panelNames: Record<ProposalSidePanel, string> = {
  comments: "Comments",
  addFee: "Add fee",
  existingFee: "Fee details",
  teamSelection: "Vendor team member selection",
};

const modeNames: Record<string, string> = {
  view: "Viewing",
  edit: "Editing",
  revise: "Revising",
  review: "Reviewing",
};

export interface ProposalPageAssistantScreen {
  /** Which of the three proposal pages this is - "Active proposal", "Draft proposal", "Archived proposal". */
  page: string;
  /** Absent until the proposal has loaded; nothing is declared before then. */
  proposal?: { id?: { value: string }; name?: { value: string } } | null;
  /** The tab route segment. */
  tab?: string;
  openPanel?: ProposalSidePanel;
  /** The comments drawer's own title, which names the field when it is scoped to one. */
  commentsTitle?: string;
  /** The page action route segment - view, edit, revise, review. */
  action?: string;
  /** The user's unsaved changes, and what they wait for — the tab chip's own words (#5812). */
  unsavedChanges?: ProposalPageChangeCount;
  /** On an active proposal, the user's saved changes that no submission has carried yet. */
  unsubmittedChanges?: ProposalPageChangeCount;
}

/** A count of the user's own changes, with the phrase the page shows for them: "not yet saved". */
export interface ProposalPageChangeCount {
  count: number;
  status: string;
}

/**
 * What a proposal page tells the assistant about itself.
 *
 * The route already carries the tab and the id; what it cannot carry is the proposal's name, the
 * drawer that is open, and whether the user has changes nobody else has seen - which is what makes
 * "why is this red?" or "what happens if I send this?" answerable without a lookup.
 */
export function proposalPageAssistantScreenContext(
  screen: ProposalPageAssistantScreen
): AssistantScreenContext | undefined {
  if (!screen.proposal) return undefined;

  const details: AssistantScreenDetail[] = [];
  const mode = screen.action ? modeNames[screen.action] : undefined;
  if (mode) details.push({ label: "Mode", value: mode });
  // Labelled with the phrase the tab chip shows, so the assistant does not call an unsaved change
  // "unsent" on a draft whose save would reach nobody.
  for (const changes of [screen.unsavedChanges, screen.unsubmittedChanges]) {
    if (changes) details.push({ label: changeCountLabel(changes.status), value: String(changes.count) });
  }

  return {
    page: screen.page,
    subject: {
      type: "proposal",
      id: screen.proposal.id?.value,
      name: screen.proposal.name?.value,
    },
    section: screen.tab ? (tabNames[screen.tab] ?? screen.tab) : undefined,
    openPanel: openPanelName(screen.openPanel, screen.commentsTitle),
    details,
  };
}

function changeCountLabel(status: string): string {
  return `Changes ${status}`;
}

function openPanelName(panel: ProposalSidePanel | undefined, commentsTitle: string | undefined): string | undefined {
  if (!panel) return undefined;
  if (panel === "comments" && commentsTitle?.trim()) return commentsTitle;
  return panelNames[panel];
}

/** Declares a proposal page's screen context for as long as the page is mounted. */
export default function useProposalPageAssistantScreenContext(screen: ProposalPageAssistantScreen): void {
  useAssistantScreenContext(proposalPageAssistantScreenContext(screen));
}
